import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import {
    BusinessModel,
    NonNullDynamicState,
    OrderType,
    ProductLayout,
    SelectorUtil,
    Shop,
    ShopContact
} from '@us/web/shared/data-access';
import * as ShopActions from './shop.actions';
import { getStringValue, ORDER_TYPES, PAYMENT_METHODS } from '@us/web/shared/utils';
import { CurrencySetting } from '@us/web/shared/data-access';
import { pick } from 'lodash';

export const SHOP_FEATURE_KEY = 'shop';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface State extends NonNullDynamicState<'shop', Shop> {}

const initialState: State = {
    shop: {
        id: '',
        name: '',
        code: '',
        address: '',
        location: null,
        email: '',
        tel: '',
        description: '',
        open_hours: [],
        status: 0,
        logo: '',
        banners: [],
        extra: '',
        options: {}
    },
    status: 'pending',
    error: null
};

export const shopFeature = createFeature({
    name: SHOP_FEATURE_KEY,
    reducer: createReducer(
        initialState,
        on(
            ShopActions.loadShop,
            (state): State => ({
                ...state,
                status: 'loading'
            })
        ),
        on(
            ShopActions.loadShopSuccess,
            (state, { shop }): State => ({
                ...state,
                shop,
                status: 'success',
                error: null
            })
        ),
        on(
            ShopActions.loadShopFailure,
            (state, { error }): State => ({
                ...state,
                status: 'error',
                error
            })
        )
    )
});

export const { name, reducer, selectShopState, selectShop } = shopFeature;

export const selectLoading = createSelector(selectShopState, SelectorUtil.isLoading);
export const selectShopId = createSelector(selectShop, shop => shop.id);
export const selectShopBanners = createSelector(selectShop, shop => shop.banners);
export const selectShopName = createSelector(selectShop, shop => shop.name);
export const selectShopLogo = createSelector(selectShop, shop => shop.logo);
export const selectShopExtra = createSelector(selectShop, shop => shop.extra);

export const selectShopOptions = createSelector(selectShop, shop => shop.options);

export const selectTopBarContent = createSelector(selectShopOptions, option =>
    getStringValue(option.top_banner_content)
);

export const selectOrderTypes = createSelector(selectShopOptions, ({ order_types }) => {
    let orderTypes = order_types ? ORDER_TYPES.filter(e => order_types.includes(e)) : [];

    if (order_types?.includes(OrderType.ShopDelivery) && !order_types?.includes(OrderType.Delivery)) {
        orderTypes = [OrderType.Delivery, ...orderTypes];
    }

    return orderTypes;
});

export const selectAllowDelivery = createSelector(selectOrderTypes, items => items.includes(OrderType.Delivery));
export const selectAllowPickup = createSelector(selectOrderTypes, items => items.includes(OrderType.Pickup));
export const selectAllowTakeAway = createSelector(selectOrderTypes, items => items.includes(OrderType.TakeAway));
export const selectAllowOrderAtTheTable = createSelector(selectOrderTypes, items =>
    items.includes(OrderType.OrderAtTheTable)
);

export const selectPaymentMethods = createSelector(selectShopOptions, ({ payment_methods }) => {
    return payment_methods ? PAYMENT_METHODS.filter(e => payment_methods.includes(e)) : [];
});

export const selectBusinessModels = createSelector(selectShopOptions, ({ business_model }) =>
    business_model === undefined ? [BusinessModel.PrePayment] : business_model
);

export const selectMainShopSettings = createSelector(selectShopOptions, ({ main_shop_settings }) =>
    main_shop_settings === undefined ? null : main_shop_settings
);

export const selectPaymentNotes = createSelector(selectShopOptions, ({ payment_notes }) =>
    getStringValue(payment_notes)
);

export const selectCurrencySetting = createSelector(
    selectShopOptions,
    shopOptions =>
        pick(shopOptions, [
            'display_decimal',
            'display_currency_unit',
            'display_reduce_price',
            'currency_unit'
        ]) as CurrencySetting
);

export const selectShopContact = createSelector(
    selectShopOptions,
    shopOptions => pick(shopOptions, ['phone', 'zalo', 'facebook_messenger', 'facebook_fanpage']) as ShopContact
);

export const selectTurnOnTheStoreChainLinkFeature = createSelector(
    selectShopOptions,
    option => option.turn_on_the_store_chain_link_feature || false
);

export const selectFooterHtml = createSelector(selectShopOptions, ({ footer_html }) => getStringValue(footer_html));

export const selectProductLayout = createSelector(
    selectShopOptions,
    ({ product_layout }) => product_layout || ProductLayout.Grid
);

export const selectShopOption = <T = any>(key: string, defaultValue?: unknown) =>
    createSelector(selectShopOptions, options => (options[key as keyof typeof options] || defaultValue) as T);

export const selectBankTransferInformation = selectShopOption<string>('bank_transfer_information');
export const selectZaloChatWidget = selectShopOption<string>('zalo_chat_widget');
export const selectTurnOnEnterNumberGuest = selectShopOption<string>('turn_on_enter_number_guest', false);
export const selectAccountVerification = selectShopOption<boolean>('account_verification', false);
export const selectAllowLoginViaSms = selectShopOption<boolean>('allow_login_via_sms', false);
export const selectAllowLoginViaZaloOa = selectShopOption<boolean>('allow_login_via_zalo_oa', false);
export const selectDefaultLanguage = selectShopOption<string>('default_language');

export const selectAllowLoginViaOtp = createSelector(
    selectAllowLoginViaSms,
    selectAllowLoginViaZaloOa,
    (a1, a2) => a1 || a2
);

export const selectAllowLoginViaFacebook = createSelector(
    selectShopOptions,
    options => options.allow_login_via_facebook && !!options.facebook_app_id
);

export const selectFacebookAppId = createSelector(selectShopOptions, options =>
    options.allow_login_via_facebook ? options.facebook_app_id : null
);

export const selectAllowLogin = createSelector(
    selectAccountVerification,
    selectAllowLoginViaFacebook,
    selectAllowLoginViaOtp,
    (accountVerification, a1, a2) => accountVerification && (a1 || a2)
);
